<script setup lang="ts">
import {computed, ref} from "vue";
import {Plus, ShieldCheck, X} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {AssistantConstraint, AssistantSubject} from "@/ai-assistant/interfaces";
import {BUFFER_BEFORE_CLOSING, NO_ACT_ON_WEEKDAY, WEEKDAYS} from "@/ai-assistant/constants";

const {constraints, subjects} = defineProps<{
    constraints: AssistantConstraint[];
    subjects: AssistantSubject[];
}>();

const emits = defineEmits<{
    (event: 'change', constraints: AssistantConstraint[]): void;
}>();

const adding = ref<boolean>(false);
const kind = ref<string>(NO_ACT_ON_WEEKDAY);
const subjectId = ref<string>('');
const weekday = ref<string>('6');
const minutes = ref<string>('30');

const subjectName = (id?: number | null): string =>
    subjects.find(subject => subject.id === id)?.name ?? 'ce sujet';

/**
 * « de » elides before a vowel: « pas d'entretien pharmaceutique », never
 * « pas de entretien pharmaceutique ». These labels are the product's voice,
 * so the grammar has to hold whatever a subject is called.
 */
const of = (name: string): string =>
    /^[aeiouyàâéèêëîïôöûüh]/i.test(name) ? `d'${name}` : `de ${name}`;

/**
 * A constraint as the pharmacist reads it, so what is guaranteed is stated in
 * their own vocabulary rather than as a data shape.
 */
const label = (constraint: AssistantConstraint): string => {
    if (constraint.kind === NO_ACT_ON_WEEKDAY) {
        const day = WEEKDAYS.find(candidate => candidate.value === constraint.weekday)?.label ?? '';

        return `Pas ${of(subjectName(constraint.subject_id).toLowerCase())} le ${day}`;
    }

    if (constraint.kind === BUFFER_BEFORE_CLOSING) {
        return `${constraint.minutes} min de battement avant la fermeture`;
    }

    return 'Contrainte non reconnue';
};

/** A buffer is unique: two different ones could not both hold. */
const hasBuffer = computed<boolean>(() =>
    constraints.some(constraint => constraint.kind === BUFFER_BEFORE_CLOSING)
);

/**
 * A second buffer could not hold, and a weekday already forbidden twice says
 * nothing more than once. Only the solver would know, and it would take the
 * first and drop the rest in silence — leaving a constraint listed as
 * guaranteed that is never applied.
 */
const duplicate = computed<boolean>(() =>
    kind.value === BUFFER_BEFORE_CLOSING
        ? hasBuffer.value
        : constraints.some(constraint =>
            constraint.kind === NO_ACT_ON_WEEKDAY
            && constraint.subject_id === Number(subjectId.value)
            && constraint.weekday === Number(weekday.value))
);

const canSubmit = computed<boolean>(() =>
    !duplicate.value && (kind.value === BUFFER_BEFORE_CLOSING ? !!minutes.value : !!subjectId.value)
);

/**
 * Back to a form that proposes something addable.
 *
 * The kind is reset too: left on a buffer that now exists, it would reopen on
 * a disabled option already selected, which is how a second one gets through.
 */
const reset = () => {
    adding.value = false;
    kind.value = NO_ACT_ON_WEEKDAY;
    subjectId.value = '';
    weekday.value = '6';
    minutes.value = '30';
};

const add = () => {
    if (!canSubmit.value) {
        return;
    }

    const added: AssistantConstraint = kind.value === BUFFER_BEFORE_CLOSING
        ? {kind: BUFFER_BEFORE_CLOSING, minutes: Number(minutes.value)}
        : {
            kind: NO_ACT_ON_WEEKDAY,
            subject_id: Number(subjectId.value),
            weekday: Number(weekday.value)
        };

    emits('change', [...constraints, added]);
    reset();
};

const remove = (index: number) => {
    emits('change', constraints.filter((_, candidate) => candidate !== index));
};
</script>

<template>
  <div>
    <div class="mb-1 flex items-center gap-1.5">
      <ShieldCheck class="size-3.5 shrink-0 text-secondary-dark" />
      <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
        Contraintes garanties
      </p>
    </div>
    <p class="mb-1.5 text-[11px] leading-snug text-gray-dark/70">
      Elles entrent dans le calcul : l'assistant ne peut pas proposer un planning qui les enfreint.
    </p>

    <ul v-if="constraints.length" class="mb-1.5 space-y-1">
      <li
        v-for="(constraint, index) in constraints"
        :key="`${constraint.kind}-${index}`"
        class="flex items-center gap-2 rounded-lg border border-secondary-dark/30 bg-secondary-lightest/40 px-2.5 py-1.5"
      >
        <span class="min-w-0 flex-1 text-xs text-gray-dark">{{ label(constraint) }}</span>
        <button
          class="shrink-0 text-gray-dark/50 transition-colors hover:text-red-600"
          :aria-label="`Retirer : ${label(constraint)}`"
          @click="remove(index)"
        >
          <X class="size-3.5" />
        </button>
      </li>
    </ul>

    <div v-if="adding" class="space-y-1.5 rounded-lg border border-primary/20 p-2">
      <Select v-model="kind">
        <SelectTrigger class="h-8 bg-white text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem :value="NO_ACT_ON_WEEKDAY" :disabled="!subjects.length">
            Interdire un acte un jour de la semaine
          </SelectItem>
          <SelectItem :value="BUFFER_BEFORE_CLOSING" :disabled="hasBuffer">
            Garder un battement avant la fermeture
          </SelectItem>
        </SelectContent>
      </Select>

      <div v-if="kind === NO_ACT_ON_WEEKDAY" class="flex gap-1.5">
        <Select v-model="subjectId">
          <SelectTrigger class="h-8 flex-1 bg-white text-xs">
            <SelectValue placeholder="Sujet" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="subject in subjects" :key="subject.id" :value="String(subject.id)">
              {{ subject.name }}
            </SelectItem>
          </SelectContent>
        </Select>
        <Select v-model="weekday">
          <SelectTrigger class="h-8 w-28 bg-white text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="day in WEEKDAYS" :key="day.value" :value="String(day.value)">
              {{ day.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div v-else class="flex items-center gap-2">
        <Input v-model="minutes" type="number" min="5" max="240" step="5" class="h-8 w-20 bg-white text-xs" />
        <span class="text-xs text-gray-dark/70">minutes avant la fermeture</span>
      </div>

      <p v-if="duplicate" class="text-[10.5px] leading-snug text-gray-dark/60">
        Cette contrainte est déjà enregistrée.
      </p>

      <div class="flex justify-end gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          class="h-7 text-xs text-gray-dark/70"
          @click="reset"
        >
          Annuler
        </Button>
        <Button
          type="button"
          size="sm"
          class="h-7 bg-primary text-xs text-white hover:bg-primary/90"
          :disabled="!canSubmit"
          @click="add"
        >
          Ajouter
        </Button>
      </div>
    </div>

    <button
      v-else
      class="flex h-7 items-center gap-1 rounded-full border border-dashed border-primary/30 px-2.5 text-[11px] text-primary/80 transition-colors hover:border-primary/60"
      @click="adding = true"
    >
      <Plus class="size-3" />
      Ajouter une contrainte
    </button>
  </div>
</template>
